import type { ApplicationFormDocument } from '@/types'
import {
  typedCollection,
  typedDoc,
  getCollection,
  getDocument,
  updateDocument,
  deleteDocument,
  addDoc,
  serverTimestamp,
  where,
} from '@/lib/firebase/firestore'

const col = () => typedCollection<ApplicationFormDocument>('applicationForms')
const doc = (id: string) => typedDoc<ApplicationFormDocument>('applicationForms', id)

export const adminFormBuilderService = {
  async getByApplicationId(
    applicationId: string
  ): Promise<(ApplicationFormDocument & { id: string }) | null> {
    const results = await getCollection(col(), where('applicationId', '==', applicationId))
    return results[0] ?? null
  },

  async getById(id: string): Promise<ApplicationFormDocument | null> {
    return getDocument(doc(id))
  },

  async create(applicationId: string): Promise<string> {
    const payload = {
      applicationId,
      sections: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(col(), payload as unknown as ApplicationFormDocument)
    return ref.id
  },

  async save(id: string, sections: ApplicationFormDocument['sections']): Promise<void> {
    await updateDocument(doc(id), {
      sections: stripUndefined(sections),
      updatedAt: serverTimestamp(),
    } as unknown as Partial<ApplicationFormDocument>)
  },

  async delete(id: string): Promise<void> {
    await deleteDocument(doc(id))
  },
}

// Firestore rejects `undefined` values. Recursively drop undefined keys from any
// object/array so loaded library fields with optional-but-absent properties (e.g.
// description, placeholder) don't poison the write.
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefined(v)) as unknown as T
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue
      out[k] = stripUndefined(v)
    }
    return out as T
  }
  return value
}
